'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import QuoteFormSteps from './QuoteFormSteps';
import TrustSignals from './TrustSignals';
import ProcessTimeline from './ProcessTimeline';
import TestimonialCarousel from './TestimonialCarousel';
import GuaranteeSection from './GuaranteeSection';

interface QuoteFormData {
  serviceType: string;
  propertyType: string;
  patioSize: string;
  address: string;
  city: string;
  zipCode: string;
  additionalServices: string[];
  specialRequirements: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredDate: string;
  preferredTime: string;
  contactMethod: string;
  hearAboutUs: string;
  urgency: string;
  budget: string;
  additionalNotes: string;
}

const RequestQuoteInteractive = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedData, setSubmittedData] = useState<QuoteFormData | null>(null);

  const handleFormSubmit = async (data: QuoteFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/send-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        setSubmitError(result.error || 'Failed to submit your quote request. Please try again.');
        return;
      }

      setSubmittedData(data);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError('A network error occurred. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewQuote = () => {
    setIsSubmitted(false);
    setSubmittedData(null);
    setSubmitError(null);
  };

  if (isSubmitted && submittedData) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          {/* Success Message */}
          <div className="bg-success/10 border-2 border-success rounded-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-success text-white flex items-center justify-center">
                  <Icon name="CheckCircleIcon" size={32} variant="solid" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-headline font-bold text-foreground mb-2">
                  Quote Request Received!
                </h2>
                <p className="text-muted-foreground font-body mb-4">
                  Thank you, {submittedData.firstName}! We've received your quote request and will respond within 24 hours via {submittedData.contactMethod}.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:+16025550123"
                    className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-body font-medium hover:bg-primary/90 transition-brand"
                  >
                    <Icon name="PhoneIcon" size={20} />
                    <span>Call Us Now</span>
                  </a>
                  <button
                    onClick={handleNewQuote}
                    className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg border-2 border-border text-foreground font-body font-medium hover:border-primary transition-brand"
                  >
                    <Icon name="DocumentTextIcon" size={20} />
                    <span>Submit Another Quote</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Request Summary */}
          <div className="bg-card rounded-lg p-8 border border-border mb-8">
            <h3 className="text-xl font-headline font-bold text-foreground mb-6">
              Your Request Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-muted-foreground font-body mb-1">Service Type</div>
                <div className="font-body font-medium text-foreground capitalize">
                  {submittedData.serviceType.replace('-', ' ')}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-body mb-1">Property Type</div>
                <div className="font-body font-medium text-foreground capitalize">
                  {submittedData.propertyType}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-body mb-1">Location</div>
                <div className="font-body font-medium text-foreground">
                  {submittedData.city}, AZ {submittedData.zipCode}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground font-body mb-1">Preferred Date</div>
                <div className="font-body font-medium text-foreground">
                  {new Date(submittedData.preferredDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="bg-card rounded-lg p-8 border border-border">
            <h3 className="text-xl font-headline font-bold text-foreground mb-6">
              What Happens Next?
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline font-bold text-sm">
                  1
                </div>
                <div>
                  <div className="font-body font-medium text-foreground mb-1">
                    Quote Preparation (Within 24 hours)
                  </div>
                  <div className="text-sm text-muted-foreground font-body">
                    Our team will review your request and prepare a detailed quote based on your specific needs.
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline font-bold text-sm">
                  2
                </div>
                <div>
                  <div className="font-body font-medium text-foreground mb-1">
                    Quote Delivery
                  </div>
                  <div className="text-sm text-muted-foreground font-body">
                    You'll receive your personalized quote via {submittedData.contactMethod} with detailed pricing and service breakdown.
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-headline font-bold text-sm">
                  3
                </div>
                <div>
                  <div className="font-body font-medium text-foreground mb-1">
                    Schedule Your Service
                  </div>
                  <div className="text-sm text-muted-foreground font-body">
                    Once you approve the quote, we'll schedule your service for your preferred date and time.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-8 text-center">
            <p className="text-muted-foreground font-body mb-4">
              Questions? We're here to help!
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <a
                href="tel:+16025550123"
                className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-brand"
              >
                <Icon name="PhoneIcon" size={20} />
                <span className="font-body font-medium">(602) 555-0123</span>
              </a>
              <a
                href="mailto:info@powerwashing.com"
                className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-brand"
              >
                <Icon name="EnvelopeIcon" size={20} />
                <span className="font-body font-medium">info@powerwashing.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Request Your Free Quote
          </h1>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Get a detailed, no-obligation quote for your patio cleaning needs. We'll respond within 24 hours with competitive pricing and expert recommendations.
          </p>
        </div>

        {/* Trust Signals */}
        <div className="mb-12">
          <TrustSignals />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form - 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg p-8 border border-border shadow-brand">
              {/* Error Banner */}
              {submitError && (
                <div className="mb-6 flex items-start space-x-3 p-4 rounded-lg bg-red-50 border border-red-200">
                  <Icon name="ExclamationCircleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 font-body">{submitError}</p>
                </div>
              )}
              <QuoteFormSteps onSubmit={handleFormSubmit} isSubmitting={isSubmitting} />
            </div>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-8">
            {/* Contact Card */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="text-lg font-headline font-bold text-foreground mb-4">
                Need Immediate Assistance?
              </h3>
              <p className="text-sm text-muted-foreground font-body mb-4">
                Call us now for urgent requests or questions about our services.
              </p>
              <a
                href="tel:+16025550123"
                className="flex items-center justify-center space-x-2 w-full px-6 py-3 rounded-lg bg-accent text-accent-foreground font-body font-semibold hover:bg-accent/90 transition-brand shadow-cta-glow mb-3"
              >
                <Icon name="PhoneIcon" size={20} />
                <span>(602) 555-0123</span>
              </a>
              <a
                href="mailto:info@powerwashing.com"
                className="flex items-center justify-center space-x-2 w-full px-6 py-3 rounded-lg border-2 border-border text-foreground font-body font-medium hover:border-primary transition-brand"
              >
                <Icon name="EnvelopeIcon" size={20} />
                <span>info@powerwashing.com</span>
              </a>
            </div>

            {/* Process Timeline */}
            <ProcessTimeline />

            {/* Guarantee Section */}
            <GuaranteeSection />

            {/* Testimonial Carousel */}
            <TestimonialCarousel />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestQuoteInteractive;